/**
 * Client-side prediction, rewind-and-replay reconciliation, remote-body
 * interpolation, and the tick-lead controller that keeps the whole thing
 * pointed at the right moment in time.
 *
 * NOTHING HERE READS A CLOCK. Every elapsed time and every round-trip estimate
 * arrives as a parameter, so this module runs unchanged in a browser, in a
 * worker and in Node — which matters because the server imports ./snapshot and
 * ./codec from the same tree and a stray `performance.now()` here would be one
 * refactor away from being imported there.
 *
 * ===========================================================================
 * THE SHAPE OF A PREDICTED CLIENT
 * ===========================================================================
 *
 * The client runs its own `World` roughly RTT/2 + jitter AHEAD of the server,
 * so that the input it produces for tick T arrives just before the server
 * simulates tick T. Every predicted tick is recorded in a ring buffer as
 * (inputSeq, the full inputs used, the serialized state BEFORE the step).
 *
 * When an authoritative snapshot for tick T arrives:
 *
 *   1. Look up the ring entry for T — that is what the client THOUGHT the world
 *      looked like at T.
 *   2. Compare it with the snapshot AT WIRE PRECISION. If every field
 *      quantizes to within a quantum of what the server sent, the prediction
 *      was right and there is nothing to do: no rewind, no replay, no
 *      correction, no allocation. This is the overwhelmingly common case and it
 *      costs one comparison pass.
 *   3. Otherwise rewind: restore the ring's serialized state for T (which
 *      carries the hidden machinery a snapshot cannot — dodge timers, the boost
 *      burst, the powerslide ramp, the RNG), overwrite the authoritative fields
 *      from the snapshot, and re-step every tick from T to the present using
 *      the inputs the ring already holds. That is exactly the unacked inputs
 *      and nothing else.
 *
 * Step 2 is the whole reason this is affordable. The simulation is
 * bit-deterministic, so a client that guessed right lands on the server's state
 * exactly; "did I guess right" is therefore a question with a crisp answer
 * rather than a threshold nobody can defend. The threshold that IS used — one
 * quantum — exists only because the snapshot arrives quantized, and it is
 * chosen so that accepting a prediction can never introduce more error than the
 * wire already did.
 *
 * ===========================================================================
 * ONLY THE LOCAL CAR IS PREDICTED
 * ===========================================================================
 *
 * The predicted world does contain the other cars and the ball — it has to,
 * because the local car collides with them — but they are stepped with the last
 * input the client saw, which is a guess and is usually wrong. They are NOT
 * what gets drawn. Remote cars and the ball are drawn from
 * `SnapshotInterpolator`, which blends between the last two snapshots it holds
 * and CLAMPS at the newest one rather than extrapolating.
 *
 * Refusing to extrapolate is a deliberate trade. Extrapolation hides latency
 * while the world is predictable and lies spectacularly when it is not: a ball
 * that has just been hit continues along its old path for the length of the
 * buffer and then teleports. Being 60 ms behind the ball is a constant, learned
 * offset; a ball that jumps at exactly the moment it becomes interesting is
 * not.
 *
 * ===========================================================================
 * SMOOTHING THE CORRECTION
 * ===========================================================================
 *
 * A rewind moves the local car. Applying that move to the rendered pose
 * immediately is a visible snap several times a second on a busy link, and the
 * player reads it as the physics being unstable rather than as the network
 * being lossy.
 *
 * `CorrectionSmoother` keeps the ERROR (where the car was going to be drawn
 * minus where it now is) and decays it geometrically, half-life 3 ticks / 25 ms
 * — under 1 % left after 30 ticks. The simulation is never touched: the blend
 * exists purely between `world.cars[i].position` and the matrix handed to the
 * renderer, so a smoothed car still collides, boosts and scores from its
 * authoritative position. Orientation is blended the same way, through the
 * axis-angle of the error rotation.
 *
 * Two escape hatches, both necessary:
 *   - a correction larger than `snapDistance` (default 300 uu) or
 *     `snapAngle` (default 60 degrees) is applied instantly. That is a
 *     demolition, a goal reset or a kickoff, and sliding a car 3000 uu across
 *     the field over a quarter of a second looks far worse than the cut.
 *   - a correction arriving while the previous one is still blending is
 *     measured from the RENDERED pose, not the simulated one, so the visible
 *     car never jumps even when corrections stack.
 *
 * ===========================================================================
 * THE LEAD IS ADJUSTED, NEVER JUMPED
 * ===========================================================================
 *
 * `TickLead` converts (rtt, jitter) into a target lead in ticks and then closes
 * the gap by running the client's simulation a few percent fast or slow —
 * `advance()` returns how many ticks to step for a given elapsed time, and the
 * rate it uses is a continuous function of the lead error. A client that is
 * eight ticks behind where it should be spends about a second catching up at
 * 110 % speed, which nobody can see. Setting the tick counter directly would
 * teleport every predicted body by the size of the correction, which everybody
 * can see.
 *
 * Past `resyncTicks` of error the controller gives up and jumps, on purpose: a
 * five-second stall would otherwise take a minute to ease out of, and a minute
 * of being wrong is worse than one visible cut.
 */

import { neutralInput, type CarInput } from '../physics/car-state';
import {
  axisToRotation,
  cloneMat3,
  clone as cloneVec,
  identity3,
  mulMat,
  rotationToAxis,
  scale as scaleVec,
  transpose,
  vec3,
  type Mat3,
  type Vec3,
} from '../physics/math';
import { TICK_RATE } from '../physics/constants';
import { deserializeState, serializeState, type SerializedCar, type SerializedState } from '../sim/state';
import type { World, WorldStepEvents } from '../sim/world';
import {
  ANGULAR_STEP,
  BOOST_STEP,
  CLOCK_RANGE,
  FLAG_DEMOLISHED,
  FLAG_HAS_FLIP,
  FLAG_IS_DODGING,
  FLAG_JUMPED,
  FLAG_ON_GROUND,
  FLAG_SUPERSONIC,
  ORIENTATION_MAX_ERROR,
  PAD_TIMER_RANGE,
  POSITION_STEP,
  VELOCITY_STEP,
  matrixToQuat,
  quantizeU16,
  quatToMatrix,
} from './codec';
import { applySnapshot } from './snapshot';
import {
  DEFAULT_JITTER_MS,
  DEFAULT_SNAPSHOT_RATE,
  INPUT_REDUNDANCY,
  MAX_CLIENT_LEAD_TICKS,
  type CarSnapshot,
  type InputPacket,
  type Snapshot,
} from './protocol';

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const cloneInput = (input: CarInput): CarInput => ({
  throttle: input.throttle,
  steer: input.steer,
  pitch: input.pitch,
  yaw: input.yaw,
  roll: input.roll,
  jump: input.jump,
  boost: input.boost,
  handbrake: input.handbrake,
});

const clampNum = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/** Angle between two unit quaternions, radians, ignoring the double cover. */
export const quatAngle = (
  a: readonly [number, number, number, number],
  b: readonly [number, number, number, number]
): number => {
  const d = Math.abs(a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3]);
  return 2 * Math.acos(clampNum(d, -1, 1));
};

// ---------------------------------------------------------------------------
// Comparing a prediction with the authority
// ---------------------------------------------------------------------------

/** The worst disagreement found between a predicted state and a snapshot. */
export interface PredictionError {
  /** Largest positional disagreement over every car and the ball, uu. */
  position: number;
  /** Largest linear-velocity disagreement, uu/s. */
  velocity: number;
  /** Largest angular-velocity disagreement, rad/s. */
  angular: number;
  /** Largest orientation disagreement, radians. */
  orientation: number;
  /** Largest boost disagreement, boost units. */
  boost: number;
  /** True when any packed boolean, boost pad, match phase or score disagrees.
   *  Discrete fields have no tolerance — they are right or they are not. */
  discrete: boolean;
}

/** Per-field acceptance thresholds. Defaults are two quanta of each budget. */
export interface PredictionTolerance {
  position: number;
  velocity: number;
  angular: number;
  orientation: number;
  boost: number;
}

/**
 * Two quanta of everything.
 *
 * One quantum would be the theoretical floor, but the server rounds a float and
 * the client rounds its own float, and two values a hair either side of a
 * quantum boundary round apart while being identical for every purpose. Two
 * quanta is the smallest threshold that cannot fire on a correct prediction.
 */
export const DEFAULT_TOLERANCE: PredictionTolerance = {
  position: 2 * POSITION_STEP,
  velocity: 2 * VELOCITY_STEP,
  angular: 2 * ANGULAR_STEP,
  orientation: 2 * ORIENTATION_MAX_ERROR,
  boost: 2 * BOOST_STEP,
};

/** The same six bits `packCarFlags` writes, read off a serialized car. */
const serializedFlags = (car: SerializedCar): number =>
  (car.onGround ? FLAG_ON_GROUND : 0) |
  (car.supersonic ? FLAG_SUPERSONIC : 0) |
  (car.demolished ? FLAG_DEMOLISHED : 0) |
  (car.hasFlip ? FLAG_HAS_FLIP : 0) |
  (car.isDodging ? FLAG_IS_DODGING : 0) |
  (car.hasJumped ? FLAG_JUMPED : 0);

const worst = (
  err: PredictionError,
  a: { x: number; y: number; z: number },
  b: readonly [number, number, number],
  field: 'position' | 'velocity' | 'angular'
): void => {
  const d = Math.max(Math.abs(a.x - b[0]), Math.abs(a.y - b[1]), Math.abs(a.z - b[2]));
  if (d > err[field]) err[field] = d;
};

/**
 * How far a stored predicted state is from an authoritative snapshot.
 *
 * Compares the SIM state against the DECODED snapshot in world units rather
 * than comparing quantized integers, so the numbers it returns are meaningful
 * to a human reading a test failure. The acceptance decision is then made
 * against thresholds expressed in the same units.
 */
export const predictionError = (state: SerializedState, snap: Snapshot): PredictionError => {
  const err: PredictionError = {
    position: 0,
    velocity: 0,
    angular: 0,
    orientation: 0,
    boost: 0,
    discrete: false,
  };

  const n = Math.min(state.cars.length, snap.cars.length);
  if (state.cars.length !== snap.cars.length) err.discrete = true;

  for (let i = 0; i < n; i++) {
    const car = state.cars[i];
    const s = snap.cars[i];
    worst(err, car.position, s.position, 'position');
    worst(err, car.velocity, s.velocity, 'velocity');
    worst(err, car.angularVelocity, s.angularVelocity, 'angular');

    const q = matrixToQuat(Float64Array.from(car.orientation) as Mat3);
    const angle = quatAngle(q, s.orientation);
    if (angle > err.orientation) err.orientation = angle;

    const boostDiff = Math.abs(car.boostAmount - s.boost);
    if (boostDiff > err.boost) err.boost = boostDiff;

    if (serializedFlags(car) !== s.flags) err.discrete = true;
  }

  worst(err, state.ball.position, snap.ball.position, 'position');
  worst(err, state.ball.velocity, snap.ball.velocity, 'velocity');
  worst(err, state.ball.angularVelocity, snap.ball.angularVelocity, 'angular');

  if (
    state.match.score[0] !== snap.match.score[0] ||
    state.match.score[1] !== snap.match.score[1]
  ) {
    err.discrete = true;
  }
  if (
    Math.abs(
      quantizeU16(state.match.clock, CLOCK_RANGE) - quantizeU16(snap.match.clock, CLOCK_RANGE)
    ) > 1
  ) {
    err.discrete = true;
  }

  for (const change of snap.padChanges) {
    const pad = state.pads[change.index];
    if (pad === undefined) continue;
    if (pad.active !== change.active) err.discrete = true;
    if (
      Math.abs(
        quantizeU16(pad.respawnTimer, PAD_TIMER_RANGE) -
          quantizeU16(change.respawnTimer, PAD_TIMER_RANGE)
      ) > 1
    ) {
      err.discrete = true;
    }
  }

  return err;
};

export const withinTolerance = (
  err: PredictionError,
  tol: PredictionTolerance = DEFAULT_TOLERANCE
): boolean =>
  !err.discrete &&
  err.position <= tol.position &&
  err.velocity <= tol.velocity &&
  err.angular <= tol.angular &&
  err.orientation <= tol.orientation &&
  err.boost <= tol.boost;

// ---------------------------------------------------------------------------
// Correction smoothing
// ---------------------------------------------------------------------------

export interface CorrectionOptions {
  /** Ticks for the visible error to halve. 3 ticks is 25 ms. */
  halfLifeTicks?: number;
  /** A correction bigger than this is applied instantly, uu. */
  snapDistance?: number;
  /** A correction bigger than this is applied instantly, radians. */
  snapAngle?: number;
}

/**
 * The residual visual offset between where the local car was going to be drawn
 * and where the authority says it is. Purely a render-layer quantity; the
 * simulation never sees it.
 */
export class CorrectionSmoother {
  private error: Vec3 = vec3(0, 0, 0);
  private errorRot: Mat3 = identity3();
  private weight = 0;
  private readonly decay: number;
  private readonly snapDistance: number;
  private readonly snapAngle: number;

  constructor(options: CorrectionOptions = {}) {
    const halfLife = options.halfLifeTicks ?? 3;
    this.decay = Math.pow(0.5, 1 / Math.max(1e-6, halfLife));
    this.snapDistance = options.snapDistance ?? 300;
    this.snapAngle = options.snapAngle ?? Math.PI / 3;
  }

  /** How much of the last correction is still being hidden, 0..1. */
  get residualWeight(): number {
    return this.weight;
  }

  /** Current visible offset, uu. Zero once a correction has blended out. */
  get residualDistance(): number {
    return Math.hypot(this.error.x, this.error.y, this.error.z) * this.weight;
  }

  /**
   * Record a correction. `before` is the pose the car had BEFORE the rewind and
   * `after` the pose it has now; the error is measured from the pose that was
   * actually being RENDERED, so stacked corrections stay continuous.
   */
  record(beforePos: Vec3, beforeRot: Mat3, afterPos: Vec3, afterRot: Mat3): void {
    const renderedPos = this.position(beforePos);
    const renderedRot = this.orientation(beforeRot);

    const dx = renderedPos.x - afterPos.x;
    const dy = renderedPos.y - afterPos.y;
    const dz = renderedPos.z - afterPos.z;
    const rot = mulMat(renderedRot, transpose(afterRot));
    const axis = rotationToAxis(rot);
    const angle = Math.hypot(axis.x, axis.y, axis.z);

    if (Math.hypot(dx, dy, dz) > this.snapDistance || angle > this.snapAngle) {
      this.reset();
      return;
    }

    this.error = vec3(dx, dy, dz);
    this.errorRot = rot;
    this.weight = 1;
  }

  /** Decay the residual by `ticks` client ticks. */
  advance(ticks = 1): void {
    if (this.weight === 0) return;
    this.weight *= Math.pow(this.decay, ticks);
    if (this.weight < 1e-3) this.reset();
  }

  reset(): void {
    this.error = vec3(0, 0, 0);
    this.errorRot = identity3();
    this.weight = 0;
  }

  /** The authoritative position, offset by what is left of the correction. */
  position(actual: Vec3): Vec3 {
    if (this.weight === 0) return cloneVec(actual);
    return vec3(
      actual.x + this.error.x * this.weight,
      actual.y + this.error.y * this.weight,
      actual.z + this.error.z * this.weight
    );
  }

  /** The authoritative orientation, pre-rotated by what is left of the error. */
  orientation(actual: Mat3): Mat3 {
    if (this.weight === 0) return cloneMat3(actual);
    return mulMat(axisToRotation(scaleVec(rotationToAxis(this.errorRot), this.weight)), actual);
  }
}

// ---------------------------------------------------------------------------
// Tick lead
// ---------------------------------------------------------------------------

export interface TickLeadOptions {
  tickRate?: number;
  /** Extra margin over RTT/2, ms. See DEFAULT_JITTER_MS in ./protocol. */
  jitterMs?: number;
  /** Hard cap on the lead the server will accept. */
  maxLeadTicks?: number;
  /** Largest speed-up/slow-down, as a fraction. 0.1 = up to 110 % / 90 %. */
  maxRateAdjust?: number;
  /** Lead error, in ticks, that produces the full rate adjustment. */
  gainTicks?: number;
  /** Lead error past which the controller jumps rather than eases. */
  resyncTicks?: number;
  /** Never return more than this many ticks from one `advance`. */
  maxTicksPerCall?: number;
}

/**
 * Decides how many simulation ticks the client should run for a given slice of
 * real time so that its lead converges on RTT/2 + jitter.
 *
 * Clock-free by construction: the caller measures the elapsed time and the RTT
 * and passes both in.
 */
export class TickLead {
  private readonly tickRate: number;
  private readonly jitterMs: number;
  private readonly maxLeadTicks: number;
  private readonly maxRateAdjust: number;
  private readonly gainTicks: number;
  private readonly resyncTicks: number;
  private readonly maxTicksPerCall: number;

  private accumulator = 0;
  private currentRate = 1;
  private currentLead = 0;
  private currentTarget = 0;
  private resyncs = 0;

  constructor(options: TickLeadOptions = {}) {
    this.tickRate = options.tickRate ?? TICK_RATE;
    this.jitterMs = options.jitterMs ?? DEFAULT_JITTER_MS;
    this.maxLeadTicks = options.maxLeadTicks ?? MAX_CLIENT_LEAD_TICKS;
    this.maxRateAdjust = options.maxRateAdjust ?? 0.1;
    this.gainTicks = options.gainTicks ?? 8;
    this.resyncTicks = options.resyncTicks ?? 45;
    this.maxTicksPerCall = options.maxTicksPerCall ?? 60;
  }

  /** Lead the client should be running at, in ticks, for this round trip. */
  target(rttMs: number): number {
    const ms = Math.max(0, rttMs) / 2 + this.jitterMs;
    const ticks = Math.ceil((ms / 1000) * this.tickRate);
    return clampNum(ticks, 1, this.maxLeadTicks);
  }

  /** Rate the controller last ran the simulation at, as a multiple of real time. */
  get rate(): number {
    return this.currentRate;
  }

  /** Lead measured on the last `advance`, in ticks. */
  get lead(): number {
    return this.currentLead;
  }

  get lastTarget(): number {
    return this.currentTarget;
  }

  /** How many times the controller has had to give up and jump. */
  get resyncCount(): number {
    return this.resyncs;
  }

  /**
   * Ticks to simulate for `elapsedSeconds` of real time.
   *
   * `serverTick` is the newest tick the client has authoritative state for;
   * `clientTick` is where its own prediction has reached.
   */
  advance(elapsedSeconds: number, rttMs: number, clientTick: number, serverTick: number): number {
    const target = this.target(rttMs);
    this.currentTarget = target;
    this.currentLead = clientTick - serverTick;
    const error = target - this.currentLead;

    if (Math.abs(error) > this.resyncTicks) {
      // Beyond easing range. Close it in one go and say so — see the header.
      this.resyncs++;
      this.accumulator = 0;
      this.currentRate = 1;
      if (error < 0) return 0;
      return Math.min(this.maxTicksPerCall, Math.round(error + elapsedSeconds * this.tickRate));
    }

    this.currentRate = 1 + clampNum(error / this.gainTicks, -this.maxRateAdjust, this.maxRateAdjust);
    this.accumulator += Math.max(0, elapsedSeconds) * this.tickRate * this.currentRate;
    const ticks = Math.min(this.maxTicksPerCall, Math.floor(this.accumulator));
    this.accumulator -= ticks;
    return ticks;
  }

  reset(): void {
    this.accumulator = 0;
    this.currentRate = 1;
  }
}

// ---------------------------------------------------------------------------
// Input redundancy
// ---------------------------------------------------------------------------

interface InputEntry {
  seq: number;
  tick: number;
  input: CarInput;
}

/**
 * The server's side of `INPUT_REDUNDANCY`: absorb packets, ignore the repeats,
 * and be able to answer "what did this player press on tick T".
 *
 * Because every packet repeats the last `INPUT_REDUNDANCY` inputs, losing any
 * single packet loses nothing at all — the next one carries the same inputs
 * again. The receiver therefore never asks for a retransmit and never stalls;
 * it just takes whatever is new out of whatever arrives, in any order.
 */
export class InputReceiver {
  private readonly ring: (InputEntry | null)[];
  private newest = -1;
  private contiguous = -1;

  constructor(readonly capacity = 512) {
    this.ring = new Array<InputEntry | null>(capacity).fill(null);
  }

  private slot(seq: number): number {
    return ((seq % this.capacity) + this.capacity) % this.capacity;
  }

  /** Absorb a packet. Returns how many of its inputs were new. */
  accept(pkt: InputPacket): number {
    let added = 0;
    const count = pkt.inputs.length;
    for (let i = 0; i < count; i++) {
      // `inputs` is newest-last, so the entry at i is (count - 1 - i) old.
      const age = count - 1 - i;
      const seq = pkt.seq - age;
      const tick = pkt.tick - age;
      if (seq < 0) continue;
      if (seq <= this.contiguous) continue;
      const at = this.slot(seq);
      const held = this.ring[at];
      if (held !== null && held.seq === seq) continue;
      this.ring[at] = { seq, tick, input: cloneInput(pkt.inputs[i]) };
      added++;
      if (seq > this.newest) this.newest = seq;
    }
    while (this.get(this.contiguous + 1) !== null) this.contiguous++;
    return added;
  }

  get(seq: number): CarInput | null {
    if (seq < 0) return null;
    const held = this.ring[this.slot(seq)];
    return held !== null && held.seq === seq ? held.input : null;
  }

  /**
   * The input the client stamped for `tick`, or null.
   *
   * A linear scan of the ring, deliberately: the ring is indexed by SEQUENCE,
   * which is the key the server folds inputs in by, and a second index keyed by
   * tick would have to be kept correct across reordering for a lookup that only
   * happens when something has gone wrong.
   */
  atTick(tick: number): CarInput | null {
    for (const held of this.ring) {
      if (held !== null && held.tick === tick) return held.input;
    }
    return null;
  }

  /** Newest sequence seen, or -1. */
  get newestSeq(): number {
    return this.newest;
  }

  /** Highest sequence such that nothing below it is missing. This is what a
   *  server puts in `Snapshot.ackedInputSeq`. */
  get ackSeq(): number {
    return this.contiguous;
  }
}

// ---------------------------------------------------------------------------
// The predicting client
// ---------------------------------------------------------------------------

export interface PredictionOptions {
  /** Index into `World.cars` this client drives. */
  carIndex: number;
  /** Ticks of prediction history to keep. Must exceed the worst round trip. */
  historyTicks?: number;
  tolerance?: Partial<PredictionTolerance>;
  correction?: CorrectionOptions;
}

export interface ReconcileResult {
  /** True when the prediction was right and nothing at all was done. */
  predicted: boolean;
  /** Ticks re-simulated. Zero whenever `predicted`. */
  replayedTicks: number;
  /** True when the client had to abandon its history and adopt the snapshot
   *  wholesale — a stall, a join, or a snapshot older than the ring. */
  reset: boolean;
  /** Ticks the client was running ahead of this snapshot. */
  lead: number;
  /** What the comparison actually found, for diagnostics and tests. */
  error: PredictionError;
}

interface RingEntry {
  tick: number;
  seq: number;
  /** The full parallel input array used to step this tick. */
  inputs: CarInput[];
  /** The local car's input, for the outbound packet. */
  input: CarInput;
  /** State BEFORE the step — i.e. the state at `tick`. */
  state: SerializedState;
}

export class PredictionClient {
  readonly carIndex: number;
  readonly smoother: CorrectionSmoother;

  private current: World;
  private readonly size: number;
  private readonly ring: (RingEntry | null)[];
  private readonly tolerance: PredictionTolerance;
  private nextSeq = 0;

  private replayedTotal = 0;
  private correctionCount = 0;
  private snapshotCount = 0;
  private resetCount = 0;

  constructor(world: World, options: PredictionOptions) {
    this.current = world;
    this.carIndex = options.carIndex;
    this.size = options.historyTicks ?? 192;
    this.ring = new Array<RingEntry | null>(this.size).fill(null);
    this.tolerance = { ...DEFAULT_TOLERANCE, ...options.tolerance };
    this.smoother = new CorrectionSmoother(options.correction);
  }

  /**
   * The predicted world.
   *
   * READ IT FRESH EVERY FRAME. A reconciliation rebuilds the world from a
   * serialized state (src/sim/state has no in-place restore), so the object
   * identity changes whenever a correction lands. Holding a reference across
   * `onSnapshot` gives you the pre-correction world, which is the same class of
   * bug the aliasing note in src/sim/state.ts warns about from the other side.
   */
  get world(): World {
    return this.current;
  }

  get tick(): number {
    return this.current.tick;
  }

  /** Sequence the next `step` will stamp. */
  get nextInputSeq(): number {
    return this.nextSeq;
  }

  get stats(): {
    snapshots: number;
    corrections: number;
    replayedTicks: number;
    resets: number;
  } {
    return {
      snapshots: this.snapshotCount,
      corrections: this.correctionCount,
      replayedTicks: this.replayedTotal,
      resets: this.resetCount,
    };
  }

  private slot(tick: number): number {
    return ((tick % this.size) + this.size) % this.size;
  }

  private entry(tick: number): RingEntry | null {
    const held = this.ring[this.slot(tick)];
    return held !== null && held.tick === tick ? held : null;
  }

  /**
   * Advance the prediction one tick.
   *
   * `inputs` is the full array parallel to `world.cars`. The client only knows
   * its own for certain; whatever it supplies for the others is its best guess
   * and is stored, so a replay reproduces the same guess and a tick that was
   * predicted correctly stays correct.
   */
  step(inputs: readonly CarInput[]): WorldStepEvents {
    const tick = this.current.tick;
    const copies = inputs.map(cloneInput);
    const local = copies[this.carIndex] ?? neutralInput();
    this.ring[this.slot(tick)] = {
      tick,
      seq: this.nextSeq++,
      inputs: copies,
      input: local,
      state: serializeState(this.current),
    };
    const events = this.current.step(copies);
    this.smoother.advance(1);
    return events;
  }

  /**
   * The next outbound input packet: the newest input plus the previous
   * `INPUT_REDUNDANCY - 1`, newest last.
   */
  outboundInput(): InputPacket {
    const newest = this.current.tick - 1;
    const inputs: CarInput[] = [];
    let seq = -1;
    for (let age = 0; age < INPUT_REDUNDANCY; age++) {
      const held = this.entry(newest - age);
      if (held === null) break;
      if (age === 0) seq = held.seq;
      inputs.unshift(held.input);
    }
    if (seq < 0) seq = this.nextSeq - 1;
    return { seq, tick: newest, inputs };
  }

  /** How many recorded inputs are newer than `ackedInputSeq`. */
  unackedInputCount(ackedInputSeq: number): number {
    let count = 0;
    for (const held of this.ring) {
      if (held !== null && held.seq > ackedInputSeq && held.tick < this.current.tick) count++;
    }
    return count;
  }

  /**
   * Fold an authoritative snapshot in. The snapshot must be COMPLETE — resolve
   * deltas through `SnapshotHistory` first, and drop the ones it cannot
   * resolve.
   */
  onSnapshot(snap: Snapshot): ReconcileResult {
    if (snap.baseTick >= 0) {
      throw new Error('PredictionClient needs a complete snapshot; resolve the delta first');
    }
    this.snapshotCount++;

    const now = this.current.tick;
    const held = this.entry(snap.tick);

    if (snap.tick > now || held === null) {
      // Either the server is ahead of us (we stalled, or we just joined) or the
      // snapshot predates everything we still hold. Neither can be replayed
      // from, so take the authority as the present and rebuild the lead from
      // there. TickLead will ease it back rather than jump.
      const before = this.localPose();
      const error = predictionError(serializeState(this.current), snap);
      applySnapshot(this.current, snap);
      this.ring.fill(null);
      this.resetCount++;
      this.smoother.record(
        before.position,
        before.orientation,
        this.current.cars[this.carIndex].position,
        this.current.cars[this.carIndex].orientation
      );
      return { predicted: false, replayedTicks: 0, reset: true, lead: now - snap.tick, error };
    }

    const error = predictionError(held.state, snap);
    if (withinTolerance(error, this.tolerance)) {
      // The common path. The client's own state is already the server's, to the
      // last bit, so touching it could only make it worse.
      return { predicted: true, replayedTicks: 0, reset: false, lead: now - snap.tick, error };
    }

    // Rewind. The ring's serialized state carries everything a snapshot cannot,
    // so restore that first and let the snapshot overwrite the authoritative
    // fields on top of it.
    const before = this.localPose();
    const restored = deserializeState(held.state, { arena: this.current.arena });
    applySnapshot(restored, snap);
    this.current = restored;

    let replayed = 0;
    for (let t = snap.tick; t < now; t++) {
      const step = this.entry(t);
      if (step === null) break;
      step.state = serializeState(this.current);
      this.current.step(step.inputs);
      replayed++;
    }

    this.correctionCount++;
    this.replayedTotal += replayed;
    const after = this.current.cars[this.carIndex];
    this.smoother.record(before.position, before.orientation, after.position, after.orientation);

    return { predicted: false, replayedTicks: replayed, reset: false, lead: now - snap.tick, error };
  }

  private localPose(): { position: Vec3; orientation: Mat3 } {
    const car = this.current.cars[this.carIndex];
    return { position: cloneVec(car.position), orientation: cloneMat3(car.orientation) };
  }

  /** The local car's pose for the renderer: authoritative, minus whatever is
   *  left of the last correction. */
  renderPose(): { position: Vec3; orientation: Mat3 } {
    const car = this.current.cars[this.carIndex];
    return {
      position: this.smoother.position(car.position),
      orientation: this.smoother.orientation(car.orientation),
    };
  }
}

// ---------------------------------------------------------------------------
// Interpolation of everything that is not the local car
// ---------------------------------------------------------------------------

export interface InterpolatedBody {
  position: Vec3;
  velocity: Vec3;
  /**
   * Carried because the BALL HAS NO ORIENTATION ON THE WIRE — nor in
   * `BallState`, because the physics does not need one. The renderer integrates
   * the visible spin from this vector every frame, so dropping it here left the
   * networked ball drawn perfectly in the right place and completely
   * motionless. Interpolating it linearly is right: it is an ordinary vector,
   * not an orientation, and between two snapshots 33 ms apart it barely turns.
   */
  angularVelocity: Vec3;
}

export interface InterpolatedCar extends InterpolatedBody {
  orientation: Mat3;
  boost: number;
  flags: number;
}

export interface InterpolatedFrame {
  /** The fractional tick this frame represents. */
  tick: number;
  cars: InterpolatedCar[];
  ball: InterpolatedBody;
}

export interface InterpolatorOptions {
  /**
   * How far behind the newest snapshot to render, in ticks. One snapshot
   * interval plus a jitter margin: at 120 Hz with 30 Hz snapshots that is 4 + 4
   * = 8 ticks, 67 ms. Smaller and a single late snapshot leaves the buffer
   * empty and the world freezes; larger and everyone else is needlessly stale.
   */
  delayTicks?: number;
  /** Snapshots retained. */
  capacity?: number;
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

const lerpTriple = (
  a: readonly [number, number, number],
  b: readonly [number, number, number],
  t: number
): Vec3 => vec3(lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t));

/** Shortest-arc quaternion interpolation. */
export const slerp = (
  a: readonly [number, number, number, number],
  b: readonly [number, number, number, number],
  t: number
): [number, number, number, number] => {
  let d = a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3];
  let bx = b[0];
  let by = b[1];
  let bz = b[2];
  let bw = b[3];
  if (d < 0) {
    d = -d;
    bx = -bx;
    by = -by;
    bz = -bz;
    bw = -bw;
  }

  // Near-parallel: the sine denominator collapses, so fall back to a normalized
  // linear blend, which is within a rounding error of the arc at that angle.
  let s0: number;
  let s1: number;
  if (d > 0.9995) {
    s0 = 1 - t;
    s1 = t;
  } else {
    const theta = Math.acos(clampNum(d, -1, 1));
    const sin = Math.sin(theta);
    s0 = Math.sin((1 - t) * theta) / sin;
    s1 = Math.sin(t * theta) / sin;
  }

  const out: [number, number, number, number] = [
    a[0] * s0 + bx * s1,
    a[1] * s0 + by * s1,
    a[2] * s0 + bz * s1,
    a[3] * s0 + bw * s1,
  ];
  const n = Math.hypot(out[0], out[1], out[2], out[3]);
  if (n > 1e-12) for (let i = 0; i < 4; i++) out[i] /= n;
  return out;
};

/**
 * A short buffer of complete snapshots, sampled at a fractional tick.
 *
 * NEVER EXTRAPOLATES. Sampling past the newest snapshot returns the newest
 * snapshot; sampling before the oldest returns the oldest. See the header for
 * why that is the right trade for a ball.
 */
export class SnapshotInterpolator {
  private readonly buffer: Snapshot[] = [];
  private readonly capacity: number;
  readonly delayTicks: number;

  constructor(options: InterpolatorOptions = {}) {
    this.delayTicks =
      options.delayTicks ??
      Math.ceil(TICK_RATE / DEFAULT_SNAPSHOT_RATE) +
        Math.ceil((DEFAULT_JITTER_MS / 1000) * TICK_RATE);
    this.capacity = options.capacity ?? 32;
  }

  /** Store a complete snapshot. Out-of-order arrivals are inserted in place. */
  push(snap: Snapshot): void {
    if (snap.baseTick >= 0) {
      throw new Error('SnapshotInterpolator needs complete snapshots');
    }
    let at = this.buffer.length;
    while (at > 0 && this.buffer[at - 1].tick > snap.tick) at--;
    if (at > 0 && this.buffer[at - 1].tick === snap.tick) {
      this.buffer[at - 1] = snap;
    } else {
      this.buffer.splice(at, 0, snap);
    }
    while (this.buffer.length > this.capacity) this.buffer.shift();
  }

  get count(): number {
    return this.buffer.length;
  }

  get newestTick(): number {
    return this.buffer.length === 0 ? -1 : this.buffer[this.buffer.length - 1].tick;
  }

  get oldestTick(): number {
    return this.buffer.length === 0 ? -1 : this.buffer[0].tick;
  }

  /** The tick a renderer should ask for: the newest, held back by the buffer. */
  renderTick(): number {
    return this.newestTick < 0 ? -1 : this.newestTick - this.delayTicks;
  }

  /** Sample at a fractional tick. Returns null while the buffer is empty. */
  sample(tick: number): InterpolatedFrame | null {
    if (this.buffer.length === 0) return null;

    const oldest = this.buffer[0];
    const newest = this.buffer[this.buffer.length - 1];
    if (tick <= oldest.tick) return frameOf(oldest, oldest, 0, oldest.tick);
    if (tick >= newest.tick) return frameOf(newest, newest, 0, newest.tick);

    let hi = 1;
    while (hi < this.buffer.length && this.buffer[hi].tick < tick) hi++;
    const b = this.buffer[hi];
    const a = this.buffer[hi - 1];
    const span = b.tick - a.tick;
    const t = span <= 0 ? 0 : (tick - a.tick) / span;
    return frameOf(a, b, t, tick);
  }
}

export interface InterpolationClockOptions {
  tickRate?: number;
  /** Largest speed-up/slow-down, as a fraction. 0.1 = up to 110 % / 90 %. */
  maxRateAdjust?: number;
  /** Error, in ticks, that produces the full rate adjustment. */
  gainTicks?: number;
  /** Error past which the clock jumps rather than eases. */
  snapTicks?: number;
}

/**
 * The fractional tick the renderer should sample the interpolator at, advanced
 * by REAL TIME rather than by snapshot arrivals.
 *
 * `SnapshotInterpolator.renderTick()` moves only when a snapshot lands — at 30
 * snapshots a second that is a 33 ms staircase, and a ball drawn from it is
 * frozen between arrivals and then jumps four ticks, while the predicted local
 * car (and the camera glued to it) moves every frame. The eye reads that
 * contrast as the ball shaking. Arrival jitter makes the steps uneven on top.
 *
 * So the sample point is a clock: it advances at tick rate scaled by the frame
 * time, and converges on `renderTick()` the same way `TickLead` converges on
 * its lead — by running a few percent fast or slow, never by stepping. A late
 * snapshot slows the ball by 10 % for a frame or two instead of freezing it;
 * an early one speeds it up instead of jumping. Both are invisible.
 *
 * Clock-free like everything here: the caller measures the elapsed time.
 */
export class InterpolationClock {
  private readonly tickRate: number;
  private readonly maxRateAdjust: number;
  private readonly gainTicks: number;
  private readonly snapTicks: number;
  private clock = -1;

  constructor(options: InterpolationClockOptions = {}) {
    this.tickRate = options.tickRate ?? TICK_RATE;
    this.maxRateAdjust = options.maxRateAdjust ?? 0.1;
    this.gainTicks = options.gainTicks ?? 4;
    this.snapTicks = options.snapTicks ?? 30;
  }

  /** The current sample tick, or -1 before the first snapshot. */
  get tick(): number {
    return this.clock;
  }

  /**
   * Advance by `elapsedSeconds` of real time, steering toward `targetTick`
   * (the interpolator's `renderTick()`), and never past `newestTick` — the
   * interpolator refuses to extrapolate, so running ahead of the newest
   * snapshot would only build up error to ease back out of.
   */
  advance(elapsedSeconds: number, targetTick: number, newestTick: number): number {
    if (targetTick < 0) {
      // Buffer empty — a join or a reset. Nothing to sample, nothing to hold.
      this.clock = -1;
      return -1;
    }
    if (this.clock < 0) {
      this.clock = targetTick;
      return this.clock;
    }

    const error = targetTick - this.clock;
    if (Math.abs(error) > this.snapTicks) {
      // A stall or a rejoin. Easing a quarter-second error out at 10 % would
      // take seconds of being visibly wrong; one cut is better.
      this.clock = targetTick;
      return this.clock;
    }

    const rate =
      1 + clampNum(error / this.gainTicks, -this.maxRateAdjust, this.maxRateAdjust);
    this.clock += Math.max(0, elapsedSeconds) * this.tickRate * rate;
    if (this.clock > newestTick) this.clock = newestTick;
    return this.clock;
  }

  reset(): void {
    this.clock = -1;
  }
}

const interpolateCar = (a: CarSnapshot, b: CarSnapshot, t: number): InterpolatedCar => ({
  position: lerpTriple(a.position, b.position, t),
  velocity: lerpTriple(a.velocity, b.velocity, t),
  angularVelocity: lerpTriple(a.angularVelocity, b.angularVelocity, t),
  orientation: quatToMatrix(slerp(a.orientation, b.orientation, t)),
  boost: lerp(a.boost, b.boost, t),
  // Booleans do not interpolate. Taking the OLDER frame's flags keeps them in
  // step with the pose being drawn, which is `a` biased: a car shown mid-jump
  // must not already be reporting itself landed.
  flags: t < 1 ? a.flags : b.flags,
});

const frameOf = (a: Snapshot, b: Snapshot, t: number, tick: number): InterpolatedFrame => ({
  tick,
  cars: a.cars.map((car, i) => interpolateCar(car, b.cars[i] ?? car, t)),
  ball: {
    position: lerpTriple(a.ball.position, b.ball.position, t),
    velocity: lerpTriple(a.ball.velocity, b.ball.velocity, t),
    angularVelocity: lerpTriple(a.ball.angularVelocity, b.ball.angularVelocity, t),
  },
});
